import { normalizeMediaUrl } from "../lib/mediaUrl.js";
import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApi } from "../hooks/useApi.js";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";
import { useIsCompact } from "../hooks/useIsCompact.js";
import ContentBlockList from "../components/section/ContentBlockList.jsx";
import ItemList from "../components/section/ItemList.jsx";
import PeopleList from "../components/section/PeopleList.jsx";
import GoalList from "../components/section/GoalList.jsx";
import RoutineList from "../components/section/RoutineList.jsx";
import AskBar from "../components/AskBar.jsx";
import AboutSheet from "../components/AboutSheet.jsx";
import { aboutSeen, markAboutSeen } from "../lib/aboutSheet.js";
import { useAuth } from "../hooks/useAuth.jsx";
import { pronounsOf, mmss, posterPosition } from "./MeetPage.jsx";
import PlayThroughPlayer from "./PlayThroughPlayer.jsx";
import PreviewBanner from "../components/PreviewBanner.jsx";
import { useIsPreviewTab } from "../hooks/useIsPreviewTab.js";
import { useSectionPlaythroughs } from "../hooks/useSectionPlaythroughs.js";
import { sectionLabel } from "../lib/sections.js";

const SLUG_TO_TYPE = {
  "meet":        "meet",
  "communicate": "communicate",
  "sensory":     "sensory",
  "passions":    "passions",
  "day":         "day",
  "working-on":  "working_on",
  "hard-times":  "hard_times",
  "health":      "health",
  "people":      "people",
  "community":   "community",
};


const STATUS_COLORS = {
  live:  "var(--color-status-live)",
  draft: "var(--color-status-draft)",
  empty: "var(--color-status-empty)",
};

function SectionContent({ section, pronouns = "" }) {
  const t = section.section_type;
  const hasBlocks = section.content_blocks?.length > 0;
  const hasItems  = section.items?.length > 0;

  if (t === "people")     return <PeopleList people={section.people} />;
  if (t === "working_on") return <GoalList goals={section.goals} pronouns={pronouns} />;
  // Health has no medication list on the DSP surface (decision, Sep 28): the
  // Medication model stays dormant; the page is prose and content blocks.
  if (t === "day")        return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <RoutineList routines={section.routines} />
      {hasBlocks && <ContentBlockList blocks={section.content_blocks} />}
    </div>
  );
  if (hasItems) return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <ItemList items={section.items} pronouns={pronouns} />
      {hasBlocks && <ContentBlockList blocks={section.content_blocks} />}
    </div>
  );
  if (hasBlocks) return <ContentBlockList blocks={section.content_blocks} />;

  // Nothing to read here. Meet already skips such sections; if someone
  // arrives by URL, the lead (if any) and the poster are the page. No notice.
  return null;
}

export default function SectionPage() {
  const { clientId, sectionSlug } = useParams();
  const compact = useIsCompact();
  const previewTab = useIsPreviewTab();
  const sectionType = SLUG_TO_TYPE[sectionSlug];

  const { data: section, loading, error, refetch } = useApi(
    sectionType ? `/clients/${clientId}/sections/${sectionType}` : null
  );
  // The poster and Ask need the person (name, pronouns) and this section's
  // scene, if the playthrough has one. Both are small and cached by the browser.
  const { data: profile }    = useApi(`/clients/${clientId}/profile`);
  // What Ask's caption used to say, said once (Nov 1): the first time she puts
  // the cursor in Ask, here or on the person's page.
  const user = useAuth()?.user;
  const [about, setAbout] = useState(false);
  // The person's pronouns in the name ("His Day"), Oct 25.
  const label = sectionType ? sectionLabel(sectionType, profile?.pronouns) : sectionSlug;
  const { data: scenesData } = useApi(`/clients/${clientId}/playthrough/scenes`);
  const introScene = (scenesData?.scenes || []).find(s => s.section_type === sectionType) || null;
  // This section's own play-through, when finalized (Oct 20), takes the
  // poster; otherwise the poster plays its scene from the introduction.
  const sectionPts = useSectionPlaythroughs(clientId, profile?.section_playthroughs, profile?.section_live);
  const spt = sectionPts[sectionType] || null;
  const scene = spt
    ? { scene_id: null, poster_url: spt.posterUrl, poster_focus: spt.posterFocus, duration_ms: spt.durationMs, own: true }
    : introScene;
  const { poss } = pronounsOf(profile);
  const clientName = profile?.display_name || "";
  // The scene plays in the poster's frame (C1), same as on Meet.
  const [playing, setPlaying] = useState(false);

  if (!sectionType) return <ErrorBanner message={`Unknown section: ${sectionSlug}`} />;
  if (loading) return <LoadingSpinner message={`Loading ${label}…`} />;
  if (error)   return <ErrorBanner message={error} onRetry={refetch} />;
  if (!section) return null;

  // Caregiver access control — non-live sections show a placeholder
  if (section.status !== "live") {
    return (
      <div style={{ ...styles.page, ...(compact ? styles.pageCompact : {}) }}>
        {previewTab && <PreviewBanner label="how this section looks to a DSP" />}
        <div style={{ ...styles.content, ...(compact ? styles.contentCompact : {}) }}>
          <Link to={`/clients/${clientId}`} style={styles.back}>← Back</Link>
          <div style={styles.notLive}>
            <p style={styles.notLiveTitle}>{label}</p>
            <p style={styles.notLiveText}>This section isn't available yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[section.status] || STATUS_COLORS.empty;

  return (
    <div style={{ ...styles.page, ...(compact ? styles.pageCompact : {}) }}>
      {previewTab && <PreviewBanner label="how this section looks to a DSP" />}
      <div style={{ ...styles.content, ...(compact ? styles.contentCompact : {}) }}>
        <Link to={`/clients/${clientId}`} style={styles.back}>← Back</Link>

        {/* Poster: this section's scene, playable on its own (B2). Without a
            scene, the section name on the accent ground — no play button. The
            old image strip is gone: the same pictures now appear in the scene,
            moving and narrated, rather than as a silent row of stills. */}
        {playing && scene?.own ? (
          <PlayThroughPlayer key="section" embedded autoplay clientId={clientId}
                             playthroughBase={spt.base} counterLabel={label}
                             onExit={() => setPlaying(false)} />
        ) : playing && scene ? (
          <PlayThroughPlayer embedded autoplay clientId={clientId} sceneId={scene.scene_id}
                             onExit={() => setPlaying(false)} />
        ) : (
          <ScenePoster scene={scene} label={label} poss={poss} onPlay={() => setPlaying(true)} />
        )}

        <header style={styles.header}>
          {!scene && (
            <div style={styles.titleRow}>
              <h1 style={{ ...styles.title, ...(compact ? styles.titleCompact : {}) }}>{label}</h1>
              <span style={{ ...styles.statusDot, background: statusColor }} title={section.status} />
            </div>
          )}
          {section.lead_paragraph && (
            <p style={styles.lead}>{section.lead_paragraph}</p>
          )}
        </header>

        <SectionContent section={section} pronouns={profile?.pronouns} />

        <AskBar clientId={clientId} clientName={clientName} sectionType={sectionType}
                pronoun={poss} pinned={compact} onFirstUse={() => { if (!aboutSeen()) setAbout(true); }} />
        <AboutSheet open={about} onClose={() => { markAboutSeen(); setAbout(false); }} name={clientName}
                    team={!!user && (profile?.viewer_capabilities || []).includes("team")}
                    signedIn={!!user} />
      </div>
    </div>
  );
}

function ScenePoster({ scene, label, poss, onPlay }) {
  if (!scene) return null;
  return (
    <button type="button" onClick={onPlay} aria-label={`Play ${label}`}
            style={{ display: "block", width: "100%", padding: 0, border: "none", background: "none", font: "inherit", textAlign: "left", cursor: "pointer" }}>
      <div style={{ ...ps.poster, background: scene.poster_url ? "#6E5C49" : "var(--color-accent-subtle)" }}>
        {scene.poster_url && <img src={normalizeMediaUrl(scene.poster_url)} alt="" style={{ ...ps.img, objectPosition: posterPosition(scene.poster_focus) }} />}
        <div style={ps.scrim} />
        <div style={ps.text}>
          <div>
            <div style={ps.title}>{label}</div>
            <div style={ps.sub}>{[mmss(scene.duration_ms), scene.own ? "a short stream" : `this part of ${poss} introduction`].filter(Boolean).join(" · ")}</div>
          </div>
          <span style={ps.play} aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 16 16" style={{ display: "block", marginLeft: 2 }}><path d="M4 2.5 L13 8 L4 13.5 Z" fill="#fff" /></svg>
          </span>
        </div>
      </div>
    </button>
  );
}

const ps = {
  poster: { position: "relative", height: 190, borderRadius: "var(--radius-lg)", overflow: "hidden" },
  img:    { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" },
  scrim:  { position: "absolute", left: 0, right: 0, bottom: 0, height: "60%", background: "linear-gradient(transparent, rgba(26,25,21,0.75))" },
  text:   { position: "absolute", left: 16, right: 16, bottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, color: "#fff" },
  title:  { fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500, lineHeight: 1.2 },
  sub:    { fontSize: "var(--text-sm)", opacity: 0.88, marginTop: 2 },
  play:   { width: 50, height: 50, borderRadius: 999, background: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};

const styles = {
  page: {
    minHeight: "100dvh",
    background: "var(--color-bg-base)",
    padding: "var(--space-6)",
  },
  pageCompact: {
    padding: "var(--space-4)",
  },
  content: {
    maxWidth: "var(--content-max-width)",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-8)",
  },
  contentCompact: {
    gap: "var(--space-6)",
  },
  back: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
    textDecoration: "none",
    // 44px touch target — a bare text link is hard to tap on a phone. Applied
    // on all sizes: a larger hit area doesn't hurt desktop and helps everywhere.
    display: "inline-flex",
    alignItems: "center",
    minHeight: "var(--tap-target-min)",
    alignSelf: "flex-start",
  },
  header: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  titleRow: { display: "flex", alignItems: "center", gap: "var(--space-3)" },
  title: {
    fontSize: "var(--text-2xl)",
    fontWeight: "var(--weight-bold)",
    color: "var(--color-text-primary)",
  },
  titleCompact: {
    fontSize: "var(--text-xl)",
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "var(--radius-full)",
    flexShrink: 0,
  },
  lead: {
    fontSize: "var(--text-base)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-loose)",
    maxWidth: 600,
  },
  empty: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-8)",
    textAlign: "center",
    color: "var(--color-text-tertiary)",
    fontSize: "var(--text-sm)",
  },
  notLive: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-10)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  },
  notLiveTitle: {
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-secondary)",
  },
  notLiveText: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
  },
};
